import {
  useLayoutEffect,
  useRef,
  useState,
  type DependencyList,
  type RefObject,
} from 'react'

/**
 * Tracks whether a scroll container's content overflows its visible height
 * (i.e. a vertical scrollbar is present). Re-measures on mount, whenever any of
 * the provided `deps` change, and whenever the element resizes (via
 * ResizeObserver).
 *
 * Useful for conditionally reserving scrollbar gutter space — e.g. only padding
 * a list on the right when it actually scrolls, so a non-scrolling list stays
 * flush.
 *
 * @param deps Values that should trigger a re-measure (e.g. open state, items).
 * @returns A tuple of [ref to attach to the scroll container, isOverflowing].
 *
 * @example
 * ```tsx
 * const [listRef, isOverflowing] = useIsOverflowing<HTMLDivElement>([isOpen, items])
 * return <div ref={listRef} css={[listStyle, isOverflowing && gutterStyle]} />
 * ```
 */
export const useIsOverflowing = <T extends HTMLElement = HTMLElement>(
  deps: DependencyList = []
): [RefObject<T>, boolean] => {
  const ref = useRef<T>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) {
      return
    }
    // +1 guards against sub-pixel rounding reporting a phantom overflow.
    const measure = () =>
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return [ref, isOverflowing]
}
